'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import { useState } from 'react';
import { MenuItem } from '../Menu/MenuItem';
import { MenuList } from '../Menu/MenuList';
import { MenuListItem } from '../Menu/MenuListItem';
import { deprecatedDropdownCssClassNames } from '../Dropdown/deprecatedDropdownCssClassNames';
import { useDeprecatedDropdownContext } from '../Dropdown/hooks/useDeprecatedDropdownContext';
import { DeprecatedDropdownMenuWrapper } from '../Dropdown/utils/DeprecatedDropdownMenuWrapper';
import { getMenuAlignment } from '../Dropdown/utils/getMenuAlignment';
import { setFlyoutDimensions } from './utils/setFlyoutDimensions';
const transforms = {
    top: 'bottom center',
    'top-start': 'bottom left',
    'top-end': 'bottom right',
    bottom: 'top center',
    'bottom-start': 'top left',
    'bottom-end': 'top right',
    left: 'right center',
    'left-start': 'right top',
    'left-end': 'right bottom',
    right: 'left center',
    'right-start': 'left top',
    'right-end': 'left bottom'
};
const DropdownMenuPopper = styled(Popper)({
    zIndex: 1300
});
export const NavFlyout = ({ children, popperModifiers: userModifiers = [], placement = 'bottom-start', align, flip, items, hasDivider, className, ...props }) => {
    const { open, anchorRef, setOpen, dropdownMenuId } = useDeprecatedDropdownContext('Menu');
    const [menuIndex, setMenuIndex] = useState(() => {
        if (items?.[0].FlyoutContent) {
            return 0;
        }
        return undefined;
    });
    const wrapperRef = (instance) => {
        if (instance) {
            setFlyoutDimensions(instance, menuIndex);
            return instance;
        }
        return null;
    };
    const resetChosenMenuItem = () => {
        setMenuIndex(0);
    };
    const resolvedPlacement = align ? getMenuAlignment(align) : placement;
    const popperModifiers = [
        {
            name: 'flip',
            enabled: Boolean(flip),
            options: {
                altBoundary: true,
                rootBoundary: 'document',
                padding: 16
            }
        },
        {
            name: 'preventOverflow',
            options: {
                padding: 16
            }
        },
        ...userModifiers
    ];
    const handleClose = () => {
        setOpen(false);
    };
    const handleMenuWrapperBlur = (event) => {
        const isAnotherTopLevelItem = event.relatedTarget === anchorRef.current?.parentElement?.nextElementSibling?.firstChild;
        if (isAnotherTopLevelItem) {
            handleClose();
        }
    };
    const handleMenuWrapperKeyDown = (event) => {
        if (event.code === 'Escape') {
            event.preventDefault();
            anchorRef.current?.focus();
            handleClose();
        }
    };
    const renderMenuItems = () => {
        return items?.map(({ label, selected: userControlledSelectedState, subNavigationItems, FlyoutContent, onClick, className: itemClassName, ...itemProps }, itemIndex) => {
            const hasFlyout = FlyoutContent;
            let isSelected = Boolean(hasFlyout) && menuIndex === itemIndex;
            if (userControlledSelectedState) {
                isSelected = userControlledSelectedState;
                setMenuIndex(itemIndex);
            }
            const handleMenuItemClick = (event) => {
                onClick?.(event);
                if (hasFlyout) {
                    event.preventDefault();
                    setMenuIndex(itemIndex);
                }
                else {
                    handleClose();
                }
            };
            return (_jsxs(MenuListItem, { children: [_jsx(MenuItem, { ...itemProps, className: clsx(deprecatedDropdownCssClassNames.menuItem, 'DropdownMenu-menuItem', `DropdownMenu-menuItem${itemIndex}`, itemClassName), onClick: handleMenuItemClick, selected: isSelected, ...hasFlyout && {
                            'aria-expanded': isSelected,
                            endIcon: _jsx(KeyboardArrowRight, {})
                        }, children: label }), hasFlyout && (_jsx(FlyoutContent, { hasDivider: hasDivider, hidden: !isSelected, items: subNavigationItems, menuTitle: label }, `menu-item-flyout-${itemIndex}`))] }, `menu-item-${itemIndex}`));
        });
    };
    return (_jsx(DropdownMenuPopper, { anchorEl: anchorRef.current, className: clsx(className, deprecatedDropdownCssClassNames.menu, 'FuelDropdownMenu-root'), disablePortal: true, modifiers: popperModifiers, open: open, placement: resolvedPlacement, role: 'presentation', transition: true, children: ({ TransitionProps, placement: childPlacement }) => (_jsx(Grow, { ...TransitionProps, onExited: () => {
                resetChosenMenuItem();
                TransitionProps?.onExited();
            }, style: {
                transformOrigin: transforms[childPlacement]
            }, children: _jsx(DeprecatedDropdownMenuWrapper, { className: deprecatedDropdownCssClassNames.menuWrapper, onBlur: handleMenuWrapperBlur, onKeyDown: handleMenuWrapperKeyDown, ref: wrapperRef, children: _jsx(ClickAwayListener, { onClickAway: handleClose, children: _jsx(MenuList, { ...props, className: deprecatedDropdownCssClassNames.menuList, hasDivider: hasDivider, id: dropdownMenuId, children: children ?? renderMenuItems() }) }) }) })) }));
};
